import { IItem } from "./items/IItem";
import {
  TypeKeys,
  TextBoxItem,
  MovableTextBoxItem,
  DateItem,
  CheckBoxItem,
  CircleSelectItem,
  RollItem,
  ComboBoxItem,
  TextItemOption,
  ImageItem,
  MovableImageItem,
  AbstractPlainTextItem,
  AbstractCheckableItem,
  AbstractOptionSelectableTextItem,
  ExternalDataItem,
  CalculateItem,
  RichTextBoxItem,
  IMksTemplateExecutable,
  IDynamicTemplateExecutable,
} from "./items";
import { FuzzyDate } from "../system";
import { FontSizeCalculator } from "../drawing";
import { DateInputMethodKinds } from "./inputMethodKinds";

/*
★★★ V1調査メモ ★★★

■転記(タイプが違うときについてはあまり考慮しなくてもよい？)
http://git.pscad.co.jp:8080/gitbucket/documaker/DocuMakerV1.5/blob/7b915035fc6c7418d11aeeeb1255676813078a36/src/findex/documaker/presentation/main/reportEditor/ReportViewControlModel.java#L820

■引用(タイプが違うときについてはあまり考慮しなくてもよい？)
http://git.pscad.co.jp:8080/gitbucket/documaker/DocuMakerV1.5/blob/7b915035fc6c7418d11aeeeb1255676813078a36/src/findex/documaker/presentation/main/reportEditor/ReportViewControlModel.java#L1357
http://git.pscad.co.jp:8080/gitbucket/documaker/DocuMakerV1.5/blob/7b915035fc6c7418d11aeeeb1255676813078a36/src/findex/documaker/presentation/main/reportEditor/ReportViewControlModel.java#L2518

・引用・転記元に続紙がある場合は続紙ごとコピーしてくる模様
http://git.pscad.co.jp:8080/gitbucket/documaker/DocuMakerV1.5/blob/7b915035fc6c7418d11aeeeb1255676813078a36/src/findex/documaker/presentation/main/reportEditor/ReportViewControlModel.java#L1324

・患者IDが違っていても引用・転記は可能な模様・・
*/

// TODO: Item側に付与する？
interface ICitableItem {
  id: string;
  name: string;
  subName: string;
  itemName: string;
}

export class OverwriteUpdater {
  private _id: string = null;
  private _name: string = "";
  private _subName: string = "";
  private _itemName: string = "";
  private _text: string = "";
  private _isChecked: boolean = false;
  private _value: any = null; // いろんな型がありうるので注意！
  private _row: number = null;
  private _column: number = null;
  private _isFreeInputCalculate = false;
  private _isManualInputCalculateDate = false;
  private _isFreeInputSelectableItem = false;
  private _linkedValue: string = null;
  private _richTextAsHtml: string = null;
  private _isMultiCheckable = false;
  private _mksTemplateRegisteredId: string = null;
  private _dynamicTemplateRegisteredId: string = null;

  constructor(item: IItem, public isByName: boolean, public shouldQuoteTemplateId: boolean) {
    switch (item.typeKey) {
    case TypeKeys.TextBoxItem:
      this.extractValueFromTextBoxItem(item as TextBoxItem);
      break;
    case TypeKeys.MovableTextBoxItem:
      this.extractValueFromMovableTextBoxItem(item as MovableTextBoxItem);
      break;
    case TypeKeys.DateItem:
      this.extractValueFromDateItem(item as DateItem);
      break;
    case TypeKeys.CheckBoxItem:
      this.extractValueFromCheckBoxItem(item as CheckBoxItem);
      break;
    case TypeKeys.CircleSelectItem:
      this.extractValueFromCircleSelectItem(item as CircleSelectItem);
      break;
    case TypeKeys.RollItem:
      this.extractValueFromRollItem(item as RollItem);
      break;
    case TypeKeys.ComboBoxItem:
      this.extractValueFromComboBoxItem(item as ComboBoxItem);
      break;
    case TypeKeys.ImageItem:
      this.extractValueFromImageItem(item as ImageItem);
      break;
    case TypeKeys.MovableImageItem:
      this.extractValueFromMovableImageItem(item as MovableImageItem);
      break;
    case TypeKeys.ExternalDataItem:
      this.extractValueFromExternalDataItem(item as ExternalDataItem);
      break;
    case TypeKeys.CalculateItem:
      this.extractValueFromCalculateItem(item as CalculateItem);
      break;
    case TypeKeys.RichTextBoxItem:
      this.extractValueFromRichTextBoxItem(item as RichTextBoxItem);
      break;
    case TypeKeys.RangeOfMotionItem:
    case TypeKeys.LineItem:
    case TypeKeys.CircleItem:
    case TypeKeys.BoxLineItem:
    case TypeKeys.TriangleItem:
    case TypeKeys.BarcodeItem:
    case TypeKeys.PositionCorrectItem:
    case TypeKeys.ExternalProcessItem:
        // 転記対象外
      break;
    default:
      console.warn("Overwrite value extract function is not implemented. typeKey: " + item.typeKey);
    }
  }

  private extractValueFromTextBoxItem(item: TextBoxItem): void {
    this._id = item.id;
    this._name = item.name;
    this._subName = item.subName;
    this._itemName = "";
    this._text = item.text();
    this._isChecked = false;
    this._value = item.value;
    this._linkedValue = item.linkedValue;
    this._mksTemplateRegisteredId = item.mksTemplateRegisteredId;
    this._dynamicTemplateRegisteredId = item.dynamicTemplateRegisteredId;
  }

  private extractValueFromMovableTextBoxItem(item: MovableTextBoxItem): void {
    this._id = item.id;
    this._name = item.name;
    this._subName = "";
    this._itemName = "";
    this._text = item.text();
    this._isChecked = false;
    this._value = item.value;
    this._mksTemplateRegisteredId = item.mksTemplateRegisteredId;
    this._dynamicTemplateRegisteredId = item.dynamicTemplateRegisteredId;
  }

  private extractValueFromDateItem(item: DateItem): void {
    this._id = item.id;
    this._name = item.name;
    this._subName = item.subName;
    this._itemName = "";
    this._text = item.text();
    this._isChecked = false;
    this._value = item.value;
    this._linkedValue = item.linkedValue;
    this._isManualInputCalculateDate = (item.calculateInformations && item.calculateInformations.length !== 0 && item.inputMethodKind !== DateInputMethodKinds.Calculate);
  }

  private extractValueFromCheckBoxItem(item: CheckBoxItem): void {
    this._id = item.id;
    this._name = item.name;
    this._subName = "";
    this._itemName = item.itemName;
    this._text = item.isChecked ? item.text : "";
    this._isChecked = item.isChecked;
    this._value = item.value;
    this._isMultiCheckable = item.isMultiCheckable;
  }

  private extractValueFromCircleSelectItem(item: CircleSelectItem): void {
    this._id = item.id;
    this._name = item.name;
    this._subName = "";
    this._itemName = item.itemName;
    this._text = item.isChecked ? item.text : "";
    this._isChecked = item.isChecked;
    this._value = item.value;
    this._isMultiCheckable = item.isMultiCheckable;
  }

  private extractValueFromRollItem(item: RollItem): void {
    this._id = item.id;
    this._name = item.name;
    this._subName = "";
    this._itemName = "";
    this._text = item.label;
    this._isChecked = false;
    this._value = item.value;
    this._isFreeInputSelectableItem = item.shouldInputFree;
  }

  private extractValueFromComboBoxItem(item: ComboBoxItem): void {
    this._id = item.id;
    this._name = item.name;
    this._subName = "";
    this._itemName = "";
    this._text = item.label;
    this._isChecked = false;
    this._value = item.value;
    this._isFreeInputSelectableItem = item.shouldInputFree;
  }

  private extractValueFromImageItem(item: ImageItem): void {
    this._id = item.id;
    this._name = item.name;
    this._subName = "";
    this._itemName = "";
    this._text = "";
    this._isChecked = false;
    this._value = null;
  }

  private extractValueFromMovableImageItem(item: MovableImageItem): void {
    this._id = item.id;
    this._name = "";
    this._subName = "";
    this._itemName = "";
    this._text = "";
    this._isChecked = false;
    this._value = null;
  }

  private extractValueFromExternalDataItem(item: ExternalDataItem): void {
    this._id = item.id;
    this._name = item.name;
    this._subName = item.subName;
    this._itemName = "";
    this._text = item.text();
    this._isChecked = false;
    this._value = item.value;
    this._row = item.row;
    this._column = item.column;
  }

  private extractValueFromCalculateItem(item: CalculateItem): void {
    this._id = item.id;
    this._name = item.name;
    this._subName = "";
    this._itemName = "";
    this._text = item.text();
    this._isChecked = false;
    this._value = item.value;
    this._isFreeInputCalculate = item.shouldInputFree;
  }

  private extractValueFromRichTextBoxItem(item: RichTextBoxItem): void {
    const plainText = item.plainText;
    this._id = item.id;
    this._name = item.name;
    this._subName = "";
    this._itemName = "";
    this._text = plainText;
    this._isChecked = false;
    this._value = plainText;
    this._richTextAsHtml = item.value;
    this._mksTemplateRegisteredId = item.mksTemplateRegisteredId;
    this._dynamicTemplateRegisteredId = item.dynamicTemplateRegisteredId;
  }

  public isKeyExist(): boolean {
    return this.isByName
      ? !!this._name
      : !!this._id;
  }

  public isKeyMatched(item: IItem): boolean {
    if (!this.isKeyExist()) { return false; }
    return this.isByName
      ? this.isNameMatched(item)
      : this.isIdMatched(item);
  }

  private isNameMatched(item: any): boolean {
    if (!this.isKeyExist()) { return false; }

    // TODO:引用・転記対象の判定について仕様の確認が必要
    const citable = item as ICitableItem;
    if (this._name !== citable.name) { return false; }
    if (item instanceof AbstractCheckableItem) {
      if (!this.isEqual(this._itemName, citable.itemName)) { return false; }
    }
    if (item instanceof ExternalDataItem) {
      if (this._row !== item.row) { return false; }
      if (this._column !== item.column) { return false; }
    } else {
      if (!this.isEqual(this._subName, citable.subName)) { return false; }
    }
    return true;
  }

  private isEqual(left: string, right: string): boolean {
    if (!left && !right) { return true; }
    return left === right;
  }

  private isIdMatched(item: any): boolean {
    if (!this.isKeyExist()) { return false; }

    // TODO:引用・転記対象の判定について仕様の確認が必要
    const citable = item as ICitableItem;
    if (this._id !== citable.id) { return false; }
    return true;
  }

  public updateValues(updateTargetItem: IItem): boolean {
    switch (updateTargetItem.typeKey) {
    case TypeKeys.TextBoxItem:
    case TypeKeys.MovableTextBoxItem:
      return this.updateTextBoxItem(updateTargetItem as TextBoxItem);
    case TypeKeys.DateItem:
      return this.updateDateItem(updateTargetItem as DateItem);
    case TypeKeys.CheckBoxItem:
      return this.updateCheckBoxItem(updateTargetItem as CheckBoxItem);
    case TypeKeys.CircleSelectItem:
      return this.updateCircleSelectItem(updateTargetItem as CircleSelectItem);
    case TypeKeys.RollItem:
      return this.updateRollItem(updateTargetItem as RollItem);
    case TypeKeys.ComboBoxItem:
      return this.updateComboBoxItem(updateTargetItem as ComboBoxItem);
    case TypeKeys.ExternalDataItem:
      return this.updateExternalDataItem(updateTargetItem as ExternalDataItem);
    case TypeKeys.CalculateItem:
      return this.updateCalculateItem(updateTargetItem as CalculateItem);
    case TypeKeys.RichTextBoxItem:
      return this.updateRichTextBoxItem(updateTargetItem as RichTextBoxItem);
    case TypeKeys.ImageItem:
    case TypeKeys.MovableImageItem:
      // ここでは更新しない  
      break;
    case TypeKeys.RangeOfMotionItem:
      // 現状の関節可動域はObjectIdが格納されていない
      // また、名称は自動で割り振られるがメンテナンスされていない
      // 転記を素直に実装すると危険のため、何らかの対策が必要
      break;
    case TypeKeys.LineItem:
    case TypeKeys.CircleItem:
    case TypeKeys.BoxLineItem:
    case TypeKeys.TriangleItem:
    case TypeKeys.BarcodeItem:
    case TypeKeys.PositionCorrectItem:
      // 引用・転記対象外
      break;
    default:
      console.warn("Overwrite value update function is not implemented. typeKey: " + updateTargetItem.typeKey);
    }
    return false;
  }

  private updateTextBoxItem(item: TextBoxItem): boolean {
    if (item.value === this._text) {
      const resultLinkedValue = this.updateTextBoxLinkedValue(item);
      const resultIsQuoted = this.updatePlainTextIsQuoted(item);
      const templateIdQuoted = this.updateTemplateRegisteredId(item);
      return resultLinkedValue || resultIsQuoted || templateIdQuoted;
    }
    item.value = this._text;
    if (item.shouldAutoAdjustHeight) {
      item.geometry.rectangle.size.height = FontSizeCalculator.calculateObjectHeight(item);
    }
    item.adjustFontSize();
    this.updateTextBoxLinkedValue(item);  // ここでは戻り値は見る必要なし
    this.updatePlainTextIsQuoted(item);
    this.updateTemplateRegisteredId(item);
    return true;
  }

  private updateTextBoxLinkedValue(item: TextBoxItem): boolean {
    if (item.linkedValue == null && this._linkedValue == null) {
      // 更新前後ともにnullもしくはundefinedなので更新不要
      return false;
    }
    if (item.linkedValue === this._linkedValue) {
      // 更新前後で同じ文字列なので更新不要
      return false;
    }
    item.linkedValue = this._linkedValue;
    return true;
  }

  private updatePlainTextIsQuoted(item: AbstractPlainTextItem): boolean {
    const old = item.isQuoted;
    item.isQuoted = item.originalValue !== this._text;
    return old !== item.isQuoted;
  }

  private updateTemplateRegisteredId(item: IMksTemplateExecutable & IDynamicTemplateExecutable): boolean {
    if (!this.shouldQuoteTemplateId) { return false; }
    let isQuoted = false;

    if (!this.isSameText(item.mksTemplateRegisteredId, this._mksTemplateRegisteredId)) {
      item.mksTemplateRegisteredId = this._mksTemplateRegisteredId;
      isQuoted = true;
    }
    if (!this.isSameText(item.dynamicTemplateRegisteredId, this._dynamicTemplateRegisteredId)) {
      item.dynamicTemplateRegisteredId = this._dynamicTemplateRegisteredId;
      isQuoted = true;
    }
    return isQuoted;
  }

  private isSameText(left: string, right: string): boolean {
    if (left == null && right == null) {
      return true;
    }
    return (left === right);
  }

  private updateDateItem(item: DateItem): boolean {
    if (item.inputMethodKind === DateInputMethodKinds.Calculate) {
      if (!this._isManualInputCalculateDate) {
        // 計算のままとする
        return false;
      }
      // 直接inputMethodKindをCalendarに変更するとカレンダーダイアログが出てしまうので対策している
      if (item.isCalendarInputtable) {
        item.setInputMethodKindAsInternal(DateInputMethodKinds.Calendar);
      } else if (item.isFreeInputable) {
        item.setInputMethodKindAsInternal(DateInputMethodKinds.FreeInput);
      } else {
        return false;
      }
    }
    // 判定が複雑なので、念のため常に更新あったものと判定している
    // コメントを外す場合はlinkedValueの更新有無も判定すること
    // if (item.value === this._value) { return false; }
    if (this._value === null || this._value === undefined) {
      item.value.value = null;
    } else if (typeof this._value === "number") {
      item.value.value = this._value.toString();
    } else if (typeof this._value === "string") {
      item.value.value = this._value;
    } else if (this._value instanceof FuzzyDate) {
      item.value.value = this._value.value;
      item.value.linkedDate = this._value.linkedDate;
    }
    item.adjustFontSize();
    item.linkedValue = this._linkedValue;
    this.updateDateIsQuoted(item);
    return true;
  }

  private updateDateIsQuoted(item: DateItem): boolean {
    const old = item.isQuoted;
    if (this._value === null || this._value === undefined) {
      item.isQuoted = item.originalValue.value !== this._value;
    } else if (typeof this._value === "number") {
      item.isQuoted = item.originalValue.value !== this._value.toString();
    } else if (typeof this._value === "string") {
      item.isQuoted = item.originalValue.value !== this._value;
    } else if (this._value instanceof FuzzyDate) {
      item.isQuoted = (item.originalValue instanceof FuzzyDate)
        ? !this.compareFuzzyDate(item.originalValue, this._value)
        : true;
    }
    return old !== item.isQuoted;
  }

  private compareFuzzyDate(a: FuzzyDate, b: FuzzyDate): boolean {
    if (a.isEmpty !== b.isEmpty) {
      return false;
    } else if (!a.isEmpty) {
      if (a.isFuzzy !== b.isFuzzy) {
        return false;
      } else if (a.isFuzzy && a.value !== b.value) {
        return false;
      } else if (!a.isFuzzy && a.value.toString() !== b.value.toString()) {
        return false;
      }
    }
    return true;
  }

  private updateCheckBoxItem(item: CheckBoxItem): boolean {
    if (item.isChecked === this._isChecked) {
      return this.updateCheckableIsQuoted(item);
    }
    if (this._isChecked && this._isMultiCheckable) {
      item.isMultiCheckable = true;
    }
    item.isChecked = this._isChecked;
    this.updateCheckableIsQuoted(item);
    return true;
  }

  private updateCircleSelectItem(item: CircleSelectItem): boolean {
    if (item.isChecked === this._isChecked) {
      return this.updateCheckableIsQuoted(item);
    }
    if (this._isChecked && this._isMultiCheckable) {
      item.isMultiCheckable = true;
    }
    item.isChecked = this._isChecked;
    this.updateCheckableIsQuoted(item);
    return true;
  }

  private updateCheckableIsQuoted(item: AbstractCheckableItem): boolean {
    const old = item.isQuoted;
    item.isQuoted = item.originalValueSource.isChecked !== this._isChecked;
    return old !== item.isQuoted;
  }

  private updateRollItem(item: RollItem): boolean {
    const selectTarget = this.getTextItemOption(item.options, this._text);
    if (!selectTarget) {
      if (!this._text) {
        return this.updateOptionSelectableTextIsQuoted(item);
      }
      if (this._isFreeInputSelectableItem && item.isFreeInputable) {
        // 選択状態をクリアしておかないと、表示している文字と不整合が発生する
        for (const menuItem of item.options) {
          menuItem.isSelected = false;
        }
        item.shouldInputFree = true;
        item.label = this._text;
        item.value = this._text;
        item.score = null;
        item.code = null;
      }
      this.updateOptionSelectableTextIsQuoted(item);
      return true;
    }

    for (const menuItem of item.options) {
      menuItem.isSelected = false;
    }
    selectTarget.isSelected = true;
    item.applyOptionValue(selectTarget);
    this.updateOptionSelectableTextIsQuoted(item);
    return true;
  }

  private updateComboBoxItem(item: ComboBoxItem): boolean {
    const selectTarget = this.getTextItemOption(item.options, this._text);
    if (!selectTarget) {
      if (!this._text) {
        return this.updateOptionSelectableTextIsQuoted(item);
      }
      // V1ではフリー入力に変更したコンボボックスのデータを引用すると、フリー入力フラグが立たずにリストの一覧にフリー入力内容が含まれる.
      // 上記データの引用を考慮し、フリー入力のフラグにかかわらず選択肢にないデータを引用する場合はフリー入力に変更してデータを反映.
      if (item.isFreeInputable) {
        // 選択状態をクリアしておかないと、表示している文字と不整合が発生する
        for (const menuItem of item.options) {
          menuItem.isSelected = false;
        }
        item.shouldInputFree = true;
        item.label = this._text;
        item.value = this._text;
        item.score = null;
        item.code = null;
      }
      this.updateOptionSelectableTextIsQuoted(item);
      return true;
    }

    for (const menuItem of item.options) {
      menuItem.isSelected = false;
    }
    selectTarget.isSelected = true;
    item.applyOptionValue(selectTarget);
    this.updateOptionSelectableTextIsQuoted(item);
    return true;
  }

  private getTextItemOption(menuItems: TextItemOption[], text: string): TextItemOption {
    for (const menuItem of menuItems) {
      if (menuItem.text === this._text) {
        return menuItem;
      }
    }
    return null;
  }

  private updateOptionSelectableTextIsQuoted(item: AbstractOptionSelectableTextItem): boolean {
    const old = item.isQuoted;
    if (typeof item.originalValueSource === "number") {
      item.isQuoted = item.options[item.originalValueSource].text !== this._text;
    } else if (typeof item.originalValueSource === "string") {
      item.isQuoted = item.originalValueSource !== this._text;
    }
    return old !== item.isQuoted;
  }

  private updateExternalDataItem(item: ExternalDataItem): boolean {
    if (item.value === this._text) {
      return this.updatePlainTextIsQuoted(item);
    }
    item.value = this._text;
    item.adjustFontSize();
    this.updatePlainTextIsQuoted(item);
    return true;
  }

  private updateCalculateItem(item: CalculateItem): boolean {
    // 引用・転記元がフリー入力のCalculateItemの場合のみフリー入力に切り替えて値を上書きする
    if (!this._isFreeInputCalculate) { return false; }
    item.shouldInputFree = true;
    item.value = this._text;
    item.adjustFontSize();
    this.updatePlainTextIsQuoted(item);
    return true;
  }

  private updateRichTextBoxItem(item: RichTextBoxItem): boolean {
    const originalValue = item.value;
    if (this._richTextAsHtml) {
      item.value = this._richTextAsHtml;
    } else {
      item.plainText = this._text;
    }
    const resultIsQuoted = this.updateRichTextBoxIsQuoted(item);
    const templateIdQuoted = this.updateTemplateRegisteredId(item);
    return resultIsQuoted || (originalValue !== item.value) || templateIdQuoted;
  }

  private updateRichTextBoxIsQuoted(item: RichTextBoxItem): boolean {
    // 新規作成時のバックエンドのデータの末尾に改行が付与される。比較時には改行が外れたものと比較するため正しく比較できない。
    // 再編集時に作成済み文書から引用する場合は問題ないため、Quillの補正が影響している模様
    const old = item.isQuoted;
    item.isQuoted = item.originalValue !== this._richTextAsHtml;
    return old !== item.isQuoted;
  }
}
